import { components, useApi } from "@appretio/api";
import { ContextSelectedOptions } from "@appretio/context-selector";
import { useInfiniteQuery } from "@tanstack/react-query";
import { SortModelItem } from "ag-grid-enterprise";
import { TemporalContext } from "../../../components/temporalContextSelector/data/temporalContextOptions";
import { PAGE_SIZE } from "../inSeasonReviewConfig";
import {
  convertOptionsToApiFormat,
  convertSortingToApiFormat
} from "../utils/convertToApiFormat";
import { inSeasonReviewQueryKeys } from "./queryKeys";

export type ReviewDataResponse =
  components["schemas"]["InSeasonReviewDataProduct"][];

export function useFetchReviewData(
  contextOptions: ContextSelectedOptions,
  temporalOptions: TemporalContext,
  sortingOptions: SortModelItem[],
  enabled: boolean = false
) {
  const apiClient = useApi();

  return useInfiniteQuery<ReviewDataResponse>({
    enabled, // Only fetch when enabled is true
    queryKey: inSeasonReviewQueryKeys.data({
      contextOptions,
      temporalOptions,
      sortingOptions
    }),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const options = convertOptionsToApiFormat(
        contextOptions,
        temporalOptions
      );

      const sorting = convertSortingToApiFormat(sortingOptions);

      try {
        const response = await apiClient.POST(
          "/v1/in-season/review/get-review-data",
          {
            body: {
              ...options,
              query_params: {
                offset: pageParam as number,
                limit: PAGE_SIZE,
                sorts: sorting
              }
            }
          }
        );
        return (response.data?.result as ReviewDataResponse) ?? [];
      } catch (error) {
        console.error("Error fetching context options:", error);
        throw error; // Let TanStack Query handle the error state
      }
    },
    getNextPageParam: (lastPage, allPages) => {
      const hasMore = lastPage.length >= PAGE_SIZE;
      const nextOffset = allPages.length * PAGE_SIZE;
      return hasMore ? nextOffset : null;
    },
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
}
